import React, { useEffect, useMemo, useState, useCallback } from "react";
import "../sass/components/modalHistoricoChamados.scss";
import { api } from "../api";

const STATUS_MAP = {
  "0": { label: "Em aberto", tone: "warning" },
  "1": { label: "Finalizado", tone: "success" },
  "": { label: "Em atendimento", tone: "info" },
};

const statusInfo = (valor) => {
  const key = (valor ?? "").toString().trim();
  return STATUS_MAP[key] || { label: key || "Indefinido", tone: "neutral" };
};

const parseBR = (valor) => {
  if (!valor) return 0;
  const match = String(valor).match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (!match) return Date.parse(valor) || 0;
  return new Date(Number(match[3]), Number(match[2]) - 1, Number(match[1])).getTime();
};

const ModalHistoricoChamados = ({ isOpen, onClose, codigoCadastro }) => {
  const [chamados, setChamados] = useState([]);
  const [chamadoSelecionado, setChamadoSelecionado] = useState(null);
  const [filtroId, setFiltroId] = useState("");
  const [ordenarAsc, setOrdenarAsc] = useState(false);
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [funcNomeMap, setFuncNomeMap] = useState({});
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState("");

  const carregarChamados = useCallback(async () => {
    if (!isOpen || !codigoCadastro) return;
    setCarregando(true);
    setErro("");

    try {
      const { data } = await api.get(`/chamados/cliente/${codigoCadastro}`, {
        params: { dataInicio: dataInicio || null, dataFim: dataFim || null },
      });
      const lista = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
      setChamados(lista);
      setChamadoSelecionado((atual) =>
        atual ? lista.find((item) => item.ID === atual.ID) || null : null,
      );
    } catch (error) {
      console.error("Erro ao buscar histórico de chamados:", error);
      setChamados([]);
      setErro(error?.response?.data?.message || "Falha ao carregar o histórico de chamados.");
    } finally {
      setCarregando(false);
    }
  }, [isOpen, codigoCadastro, dataInicio, dataFim]);

  useEffect(() => {
    if (!isOpen) return;
    const hoje = new Date();
    const inicio = new Date();
    inicio.setDate(hoje.getDate() - 30);
    setDataInicio(inicio.toISOString().split("T")[0]);
    setDataFim(hoje.toISOString().split("T")[0]);
    setFiltroId("");
    setChamadoSelecionado(null);
  }, [isOpen]);

  useEffect(() => {
    carregarChamados();
  }, [carregarChamados]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (event) => event.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen || !chamados.length) return;
    const ids = Array.from(
      new Set(
        chamados
          .flatMap((item) => [item.CodFuncFinal, item.CodFuncAbertura, item.CodFunc])
          .map(Number)
          .filter(Number.isInteger),
      ),
    );
    if (!ids.length) return;

    api
      .get("/func/names", { params: { ids: ids.join(",") } })
      .then(({ data }) => setFuncNomeMap(data?.map || data || {}))
      .catch(() => setFuncNomeMap({}));
  }, [isOpen, chamados]);

  const listaFiltradaOrdenada = useMemo(() => {
    return [...chamados]
      .filter((item) => (filtroId ? String(item.ID || "").includes(filtroId) : true))
      .sort((a, b) =>
        ordenarAsc ? parseBR(a.Data) - parseBR(b.Data) : parseBR(b.Data) - parseBR(a.Data),
      );
  }, [chamados, filtroId, ordenarAsc]);

  if (!isOpen) return null;

  const sel = chamadoSelecionado;
  const preferNome = (...valores) => {
    let numero = "";
    for (const raw of valores) {
      const valor = String(raw ?? "").trim();
      if (!valor) continue;
      if (/[a-zA-ZÀ-ÖØ-öø-ÿ]/.test(valor)) return valor;
      if (!numero && /^\d+$/.test(valor)) numero = valor;
    }
    return numero || "—";
  };

  const abertoPor = sel
    ? preferNome(sel.AbertoPorNome, sel.AbertoPor, sel.Func, funcNomeMap[sel.CodFuncAbertura], funcNomeMap[sel.CodFunc])
    : "—";
  const finalizadoPor = sel
    ? preferNome(sel.FinalizadoPorNome, sel.FinalizadoPor, funcNomeMap[sel.CodFuncFinal], sel.TecAtendimento, sel.CodFuncFinal)
    : "—";
  const origemProjeto = String(sel?.DM_ORIGEM || "").toUpperCase() === "RETORNO_PROJETO";

  return (
    <div className="mhc-overlay" onMouseDown={onClose}>
      <section className="mhc-dialog" role="dialog" aria-modal="true" aria-labelledby="mhc-title" onMouseDown={(event) => event.stopPropagation()}>
        <header className="mhc-head">
          <div><span className="mhc-eyebrow">Cliente #{codigoCadastro}</span><h2 id="mhc-title">Histórico de chamados</h2><p>Consulte atendimentos, soluções e retornos de projetos.</p></div>
          <button className="mhc-close" onClick={onClose} aria-label="Fechar">×</button>
        </header>

        <div className="mhc-filters">
          <label className="field"><span>ID</span><input type="search" placeholder="Filtrar por ID" value={filtroId} onChange={(event) => setFiltroId(event.target.value)} /></label>
          <label className="field"><span>De</span><input type="date" value={dataInicio} onChange={(event) => setDataInicio(event.target.value)} /></label>
          <label className="field"><span>Até</span><input type="date" value={dataFim} onChange={(event) => setDataFim(event.target.value)} /></label>
          <button className="btn sort" onClick={() => setOrdenarAsc((valor) => !valor)}>Data {ordenarAsc ? "↑" : "↓"}</button>
          <button className="btn refresh" onClick={carregarChamados} disabled={carregando}>{carregando ? "Atualizando..." : "Atualizar"}</button>
        </div>

        <div className="mhc-legend"><span className="chip chip-warning">Em aberto</span><span className="chip chip-success">Finalizado</span><span className="chip chip-info">Em atendimento</span><span className="chip chip-project">Retorno de projeto</span></div>

        {erro ? <div className="mhc-error">{erro}</div> : null}

        <div className="mhc-workspace">
          <div className="mhc-tablewrap">
            {carregando && !chamados.length ? (
              <div className="mhc-empty">Carregando histórico...</div>
            ) : listaFiltradaOrdenada.length === 0 ? (
              <div className="mhc-empty">Nenhum chamado encontrado no período.</div>
            ) : (
              <table className="mhc-table">
                <thead><tr><th>ID</th><th>Data</th><th>Descrição</th><th className="col-status">Status</th></tr></thead>
                <tbody>
                  {listaFiltradaOrdenada.map((item) => {
                    const status = statusInfo(item.StatusChamado);
                    const retornoProjeto = String(item.DM_ORIGEM || "").toUpperCase() === "RETORNO_PROJETO";
                    return (
                      <tr key={item.ID} onClick={() => setChamadoSelecionado(item)} className={sel?.ID === item.ID ? "is-selected" : ""}>
                        <td><strong>#{item.ID}</strong>{retornoProjeto ? <span className="mhc-row-origin">Projeto #{item.ID_PROJETO_ORIGEM}</span> : null}</td>
                        <td className="col-date">{item.Data || "—"}</td>
                        <td><span className="ellipsis" title={item.DescrAtend || ""}>{item.DescrAtend || "—"}</span></td>
                        <td className="col-status"><span className={`chip chip-${status.tone}`}>{status.label}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          <aside className={`mhc-details ${sel ? "has-selection" : ""}`}>
            {!sel ? (
              <div className="mhc-empty">Selecione um chamado para consultar os detalhes.</div>
            ) : (
              <>
                <div className="details-head"><div><small>Atendimento</small><h3>Chamado #{sel.ID}</h3></div><span className={`chip chip-${statusInfo(sel.StatusChamado).tone}`}>{statusInfo(sel.StatusChamado).label}</span></div>
                {origemProjeto ? <div className="mhc-project-origin"><strong>Retorno do projeto #{sel.ID_PROJETO_ORIGEM}</strong><span>Chamado original #{sel.ID_CHAMADO_PAI || "—"} · Ciclo {sel.NR_CICLO_PROJETO || 1}</span></div> : null}
                <div className="grid">
                  <div className="row"><span className="k">Data</span><span className="v">{sel.Data || "—"}</span></div>
                  <div className="row"><span className="k">Aberto por</span><span className="v">{abertoPor}</span></div>
                  <div className="row"><span className="k">Finalizado por</span><span className="v">{finalizadoPor}</span></div>
                  <div className="row"><span className="k">Finalização</span><span className="v">{sel.DataFim || sel.DataFinalizacao || sel.DataEncerramento || "—"}</span></div>
                  <div className="row col-2"><span className="k">Descrição</span><span className="v multiline">{sel.DescrAtend || "—"}</span></div>
                  <div className="row col-2"><span className="k">Solução</span><span className="v multiline">{sel.SolucaoAtend || "—"}</span></div>
                </div>
                <div className="history"><h4>Histórico de descrições</h4>{sel.Historico?.length ? <ul>{sel.Historico.map((item, index) => <li key={index}><time>{item.data}</time><span>{item.descricao}</span>{item.tipo ? <em>{item.tipo}</em> : null}</li>)}</ul> : <div className="mhc-empty small">Nenhuma descrição registrada.</div>}</div>
              </>
            )}
          </aside>
        </div>

        <footer className="mhc-foot"><span className="total">Total localizado: <strong>{listaFiltradaOrdenada.length}</strong></span><button className="btn secondary" onClick={onClose}>Fechar</button></footer>
      </section>
    </div>
  );
};

export default ModalHistoricoChamados;
